import SpatialAdjustEntityType = ToroEnums.SpatialAdjustEntityType;

import { Observable, of, Subject } from 'rxjs';
import { SaBannerInfo, SaBannerMsgAlignment, SaBannerType } from '../../spatial-adjust/models/sa-banner-info.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppInjector } from '../../demo/demo.module';
import { AuthManagerService } from '../auth/auth-manager.service';
import { BroadcastService } from '../../common/services/broadcast.service';
import { DemoModeMockDataService } from '../../demo/demo-mode-mock-data.service';
import { environment } from '../../../environments/environment';
import { GetCoursesCourse } from '../lynx-cloud/models/mqtt-get-courses/get-courses-course.model';
import { ILynxProxyService } from './ILynxProxyService';
import { Injectable } from '@angular/core';
import { LoggerService } from '../../common/services/logger.service';
import { LynxCloudCourseItem } from '../../core/dashboard/widgets/lynx/widget-lynx-cloud/models/lynx-cloud-course-item.model';
import { LynxCloudService } from '../lynx-cloud/lynx-cloud.service';
import { LynxDriveService } from '../lynx-drive/lynx-drive.service';
import { PingResponse } from '../lynx-cloud/models/mqtt-ping/ping-response.model';
import { SaLynxStationAdjustItem } from '../lynx-cloud/models/sa-station-adjust-item.model';
import { StationStatuses } from '../lynx-cloud/models/station-status/station-statuses.model';
import { ToroEnums } from '../../common/enumerations/toro.enums';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class LynxProxyService implements ILynxProxyService {

    // Subjects to broadcast updates
    public stationAdjustListUpdated = new Subject<SaLynxStationAdjustItem[]>();
    public errorRetrievingLynxData = new Subject<string>();
    public lynxTimeoutError = new Subject<Error>();

    public isLynxCloudUser = true;

    private demoModeMockDataService: DemoModeMockDataService;
    private lynxProxyService: ILynxProxyService;
    private _currentLynxCourseInfo: LynxCloudCourseItem;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private authManager: AuthManagerService,
                private broadcastService: BroadcastService,
                protected logger: LoggerService
    ) {
        if (environment.isDemoMode) {
            this.demoModeMockDataService = AppInjector.get(DemoModeMockDataService);
            return;
        }

        // Check if user is a Lynx Cloud or Lynx Drive user.
        this.isLynxCloudUser = !this.authManager.dashAuthenticatedUser?.isLynxDriveUser;
        this.lynxProxyService = this.isLynxCloudUser ? AppInjector.get(LynxCloudService) : AppInjector.get(LynxDriveService);

        logger.info(`Is Lynx ${this.isLynxCloudUser ? 'Cloud' : 'Drive'} User`);

        this.lynxProxyService.stationAdjustListUpdated
            .pipe(untilDestroyed(this))
            .subscribe((stationAdjustList: SaLynxStationAdjustItem[]) => {
                this.stationAdjustListUpdated.next(stationAdjustList);
            });

        this.lynxProxyService.errorRetrievingLynxData
            .pipe(untilDestroyed(this))
            .subscribe((error: string) => {
                this.errorRetrievingLynxData.next(error);
            });

        this.lynxProxyService.lynxTimeoutError
            .pipe(untilDestroyed(this))
            .subscribe((error: Error) => {
                this.notifyLynxConnectionLoss();
            });
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================


    public notifyLynxConnectionLoss() {
        this.broadcastService.saShowTopBanner.next(new SaBannerInfo({
            type: SaBannerType.Warning,
            message: 'SPATIAL_ADJUST.CONNECTION_LOST_MSG',
            messageAlignment: SaBannerMsgAlignment.Center
        }));
    }

    public clearLynxConnectionLoss() {
        this.broadcastService.saShowTopBanner.next(null);
    }

    public get currentLynxCloudCourseInfo(): LynxCloudCourseItem {
        return this._currentLynxCourseInfo;
    }

    // =========================================================================================================================================================
    // Interface Members
    // =========================================================================================================================================================

    public get isInitialized(): boolean {
        if (environment.isDemoMode) { return true; }

        return this.lynxProxyService.isInitialized;
    }

    public pingLynx(courseItem: LynxCloudCourseItem): Observable<PingResponse> {
        if (environment.isDemoMode) { return; }

        // Cache the current Lynx Course Info.
        this._currentLynxCourseInfo = courseItem;

        return this.lynxProxyService.pingLynx(courseItem);
    }

    public getCourseList(): Observable<GetCoursesCourse[]> {
        if (environment.isDemoMode) {
            return of(this.demoModeMockDataService.lynxCloudCourses);
        }

        return this.lynxProxyService.getCourseList();
    }

    public getStationStatuses(courseItem: LynxCloudCourseItem): Observable<StationStatuses> {
        if (environment.isDemoMode) {
            return of(this.demoModeMockDataService.lynxCloudStationStatuses);
        }

        return this.lynxProxyService.getStationStatuses(courseItem);
    }

    public requestSpatialAdjustData(courseItem: LynxCloudCourseItem = null): void {
        // Don't call out to Lynx Cloud/Drive in Demo Mode.
        if (environment.isDemoMode) { return; }

        this.lynxProxyService.requestSpatialAdjustData(courseItem);
    }

    public get stationAdjustList(): SaLynxStationAdjustItem[] {
        // TODO - Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return this.demoModeMockDataService.spatialAdjustStationAdjustList; }

        return this.lynxProxyService.stationAdjustList;
    }

    public requestChangePercentAdjust(entityType: SpatialAdjustEntityType = SpatialAdjustEntityType.ProgramStation, percentAdjust: number, ids: number[]): void {
        this.lynxProxyService.requestChangePercentAdjust(entityType, percentAdjust, ids);
    }
}
